import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, TextField, MenuItem, 
  IconButton, Button
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';

// Import BaseFieldControl mà bạn đã cung cấp
import BaseFieldControl from './BaseFieldControl';
import Select from "./ManagedDropdown";

export default function JsonObjectControl({ 
  data = {}, 
  onChange, 
  isReadOnly = false 
}) {
  // ================= 1. STATE QUẢN LÝ DANH SÁCH KEY-VALUE =================
  const [pairs, setPairs] = useState([]);

  // Đồng bộ dữ liệu ban đầu (data prop) vào state nội bộ
  useEffect(() => {
    // Tránh bị re-render lặp vô hạn bằng cách so sánh dữ liệu hiện tại
    const currentData = {};
    pairs.forEach(p => { if (p.key.trim()) currentData[p.key.trim()] = p.value; });
    
    if (JSON.stringify(currentData) !== JSON.stringify(data)) {
      const initialPairs = [];
      Object.keys(data || {}).forEach(k => {
        const val = data[k];
        let type = 'String';
        
        // Đoán kiểu dữ liệu từ value để render đúng control
        if (typeof val === 'number') type = 'Number';
        else if (typeof val === 'boolean') type = 'Boolean';
        else if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}/.test(val)) type = 'Date';
        
        initialPairs.push({ 
          id: Math.random().toString(36).substring(2), 
          key: k, 
          value: val, 
          type 
        });
      });
      setPairs(initialPairs);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // ================= 2. HÀM XỬ LÝ SỰ KIỆN =================
  const notifyChange = (newPairs) => {
    const resultObj = {};
    newPairs.forEach(p => {
      if (p.key.trim()) {
        resultObj[p.key.trim()] = p.value;
      }
    });
    if (onChange) onChange(null, resultObj); // Trả về dạng onChange(e, data)
  };

  const handleAdd = () => {
    const newPairs = [
      ...pairs, 
      { id: Math.random().toString(36).substring(2), key: '', value: '', type: 'String' }
    ];
    setPairs(newPairs);
    notifyChange(newPairs);
  };

  const handleRemove = (id) => {
    const newPairs = pairs.filter(p => p.id !== id);
    setPairs(newPairs);
    notifyChange(newPairs);
  };

  const handleChange = (id, fieldName, val) => {
    const newPairs = pairs.map(p => {
      if (p.id === id) {
        const updated = { ...p, [fieldName]: val };
        
        // Tự động reset value tương thích với kiểu dữ liệu mới nếu đổi Type
        if (fieldName === 'type' && p.type !== val) {
          if (val === 'Boolean') updated.value = false;
          else if (val === 'Number') updated.value = 0;
          else updated.value = '';
        }
        return updated;
      }
      return p;
    });
    setPairs(newPairs);
    notifyChange(newPairs);
  };

  // ================= 3. RENDER GIAO DIỆN =================
  return (
    <Box 
      sx={{ 
        border: '1px solid #e0e0e0', 
        borderRadius: 2, 
        p: 2, 
        bgcolor: isReadOnly ? 'action.hover' : 'background.paper' 
      }}
    >
      {/* HEADER TÍNH NĂNG */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="body2" fontWeight="bold" color="text.secondary">
          Dữ liệu Đối tượng (JSON)
        </Typography>
        {!isReadOnly && (
          <Button 
            size="small" 
            startIcon={<AddIcon />} 
            variant="outlined" 
            onClick={handleAdd}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            Thêm thuộc tính
          </Button>
        )}
      </Box>
      
      {/* KHU VỰC DỮ LIỆU */}
      {pairs.length === 0 ? (
         <Typography variant="body2" color="text.secondary" align="center" fontStyle="italic" sx={{ py: 2 }}>
           Chưa có dữ liệu
         </Typography>
      ) : (
        pairs.map((pair) => (
          <Box key={pair.id} sx={{ display: 'flex', gap: 1.5, mb: 1.5, alignItems: 'flex-start' }}>
            
            {/* 1. Ô NHẬP KEY (Tên thuộc tính) */}
            <TextField 
              size="small" 
              label="Tên Key"
              placeholder="VD: age, isActive..."
              value={pair.key} 
              onChange={(e) => handleChange(pair.id, 'key', e.target.value)}
              disabled={isReadOnly}
              sx={{ width: { xs: '35%', md: '25%' } }}
            />

            {/* 2. Ô CHỌN TYPE (Kiểu dữ liệu) */}
            <Select
              size="small"
              value={pair.type}
              onChange={(e) => handleChange(pair.id, 'type', e.target.value)}
              disabled={isReadOnly}
              sx={{ width: { xs: '25%', md: '20%'} }}
              type="select"
            >
              <MenuItem value="String">Chuỗi</MenuItem>
              <MenuItem value="Number">Số</MenuItem>
              <MenuItem value="Boolean">Luận lý</MenuItem>
              <MenuItem value="Date">Ngày</MenuItem>
            </Select>

            {/* 3. Ô NHẬP VALUE (Sử dụng BaseFieldControl linh hoạt theo Type) */}
            <Box sx={{ flexGrow: 1 }}>
              <BaseFieldControl
                field={{ type: pair.type, label: 'Giá trị' }} // Mock field ảo cho BaseFieldControl
                value={pair.value}
                onChange={(val) => {
                  // Đề phòng BaseFieldControl trả về event (như TextField chuẩn)
                  const finalVal = (val && val.target !== undefined) ? val.target.value : val;
                  handleChange(pair.id, 'value', finalVal);
                }}
                isReadOnly={isReadOnly}
              />
            </Box>

            {/* 4. NÚT XÓA */}
            {!isReadOnly && (
              <IconButton 
                color="error" 
                onClick={() => handleRemove(pair.id)} 
                sx={{ mt: 0.25, bgcolor: 'error.lighter' }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
            
          </Box>
        ))
      )}
    </Box>
  );
}